/**
 * Desktop-only "remote" capture channel for hybrid meetings.
 *
 * The mic is always the main ("room") track. This hook adds the system-audio
 * ("remote") track — the other participants — and is built around one rule the
 * user reasoned out: **lock onto the meeting's output once, don't flap.**
 *
 *   1. On record start we DON'T open a remote channel. We "hunt": probe every
 *      output's level every few seconds — minus the microphone outputs, which are
 *      excluded up front (virtual mixers like SteelSeries Sonar expose the mic as
 *      a render endpoint and flag it the Communications default, so the hunt would
 *      otherwise lock onto your own voice — see `lib/remote-mic-gate`).
 *   2. The moment a (non-mic) output produces sustained real audio we LOCK onto it
 *      — start the tap → MediaRecorder — and stop hunting. (Several active at once
 *      → prefer the Communications endpoint, where call apps route.)
 *   3. After locking we NEVER auto-switch again. Silence is normal (between
 *      speakers); only a manual pick (the meter picker) changes the source.
 *
 * Durable file: every recorder chunk is pushed into a dedicated upload buffer
 * (`chunksRef`) and assembled into `{id}.remote.webm` from REST chunk uploads —
 * the single source of truth, online or offline. The `?channel=remote` WebSocket
 * is opened only while streaming and carries the live transcript only; it is no
 * longer tee'd to disk. So a fully-offline desktop recording keeps the far-end.
 *
 * Consequences: an in-person meeting (no system audio) simply never creates a
 * remote track — no warnings, nothing to dismiss. A hybrid meeting locks on first
 * remote audio. No flapping. A manual re-pick swaps only the native source (fresh
 * channel, same recorder) so the recording is never interrupted.
 *
 * No-ops outside Tauri.
 */
import { notifications } from "@mantine/notifications"
import { useCallback, useEffect, useRef, useState } from "react"
import { analyzeEchoCoherence, type IEchoCoherenceResult } from "../lib/coherence"
import { pickRecorderMimeType } from "../lib/recorder-mime"
import { micOutputIds } from "../lib/remote-mic-gate"
import { IS_TAURI } from "../lib/runtime-origin"
import {
  ACTIVE_THRESHOLD,
  type ITapLevel,
  type ITapTarget,
  listTapTargets,
  probeTapLevels,
  type TRemotePhase,
  targetName,
} from "../lib/system-audio"
import { RECORDER_AUDIO_BITS_PER_SECOND } from "./useAudioPipeline"
import { useRemoteTapStream } from "./useRemoteTapStream"
import { useStreamingWs } from "./useStreamingWs"

type TRecordingState = "idle" | "recording" | "paused" | "finalizing"

export interface IRemoteStatus {
  /** idle (not recording) · detecting (hunting for remote audio) · capturing (locked). */
  phase: TRemotePhase
  /** Output id currently captured (undefined while hunting). */
  sourceId: string | undefined
  /** Friendly name of the captured output. */
  sourceName: string
  /** All available outputs (for the picker). */
  targets: ITapTarget[]
  /** Latest per-output peak levels (hunting probe). */
  levels: ITapLevel[]
}

const IDLE_STATUS: IRemoteStatus = { phase: "idle", sourceId: undefined, sourceName: "", targets: [], levels: [] }

/** How often the hunt probes outputs (only runs until we lock). */
const MONITOR_INTERVAL_MS = 2500
/** Sustained active probes before locking onto an output (debounce). */
const LOCK_TICKS = 2

/** How often the observe-only far-end coherence estimate is recomputed. */
const COHERENCE_INTERVAL_MS = 750
/**
 * Analyser time-domain window fed to the coherence detector, in samples. 32768 is
 * the AnalyserNode maximum — ~0.68s at 48 kHz — long enough for many Welch frames
 * and to span the echo delay, short enough to react to a mid-call output switch.
 */
const COHERENCE_FFT_SIZE = 32768
/** Empty/idle result — no loopback stream yet, or after teardown. */
const FAR_END_IDLE: IEchoCoherenceResult = { mode: "unknown", confidence: 0 }
/**
 * Consecutive `far-end-in-mic` coherence verdicts (~750ms each) required before we
 * LATCH the meeting to speakers-mode. Hysteresis against a transient false verdict;
 * ~3s of sustained agreement. Whole-session latch — once speakers, we stay
 * speakers (a mid-call switch to headphones is a v2 concern; the far-end is still
 * captured to `remote.webm` and surfaces in the final either way).
 */
const SPEAKERS_LATCH_STREAK = 4

interface IProps {
  meetingId: string
  companyId: string | null | undefined
  languages: string[]
  backend?: string | null
  attachWs?: (ws: WebSocket) => void
  detachWs?: () => void
  /**
   * Label of the user's selected primary mic (getUserMedia device label). Used to
   * exclude the mic from the remote far-end hunt — virtual mixers (SteelSeries
   * Sonar, VoiceMeeter, …) expose the mic as a render endpoint and flag it as the
   * Communications default, which the hunt would otherwise lock onto. See
   * `lib/remote-mic-gate`.
   */
  primaryMicLabel?: string
  /**
   * Buffer of the far-end ("remote") channel's MediaRecorder blobs — owned by a
   * dedicated {@link useChunkUpload} instance. Every recorder chunk is pushed here
   * so the durable `{id}.remote.webm` is assembled from REST chunk uploads (the
   * single source of truth), online or offline. The WS, when open, only carries
   * the live transcript.
   */
  chunksRef: React.MutableRefObject<Blob[]>
  /** The remote uploader's active mime type — set once the recorder mime is picked. */
  mimeTypeRef: React.MutableRefObject<string>
  /** Capture-time offset (s) written on lock; sent with the first remote chunk. */
  offsetSecRef: React.MutableRefObject<number | undefined>
  /** Room recording start (epoch ms). The offset is `now − this` at lock time. */
  recordingStartRef: React.MutableRefObject<number>
  /** True while in live/streaming mode — gates whether the remote WS is opened. */
  streamingRef: React.MutableRefObject<boolean>
  /**
   * The RAW microphone MediaStream (pre-boost/compressor — a compressor is
   * non-linear and would degrade coherence). Analysed alongside the loopback to
   * detect, by waveform coherence, whether the far-end is bleeding into the mic
   * (speakers) or not (headphones). Optional and observe-only — a later wave
   * consumes {@link IUseRemoteChannelStreamingResult.farEndInMic}; nothing here
   * changes capture or routing.
   */
  micStreamRef?: React.MutableRefObject<MediaStream | null>
  /**
   * Phase 2 conditional-loopback switch (resolved org→platform→default-on). When
   * true AND the meeting latches to speakers-mode (the mic already carries the
   * far-end), the remote LIVE transcript WS is suppressed to stop real-time
   * doubling. Capture (`remote.webm` via REST chunks) is NEVER affected. When
   * false, the remote WS behaves exactly as before.
   */
  conditionalLoopbackEnabled?: boolean
  /**
   * Reports the settled conditional-loopback verdict so the backend can gate the
   * playback mix / finalize. Fired once, with `true`, when the meeting latches to
   * speakers-mode. A meeting that never latches is left unreported (null column ⇒
   * the backend gates no-op, identical to headphones). Independent of
   * `conditionalLoopbackEnabled` — persisting the verdict is harmless when the
   * switch is off. Best-effort; the callee fire-and-forgets.
   */
  onFarEndVerdict?: (speakersMode: boolean) => void
  /** Fired when the far-end locks: start the remote periodic-save + upload timers. */
  onLock: () => void
  /** Fired when the far-end capture chain tears down: stop the remote timers. */
  onStop: () => void
}

interface IUseRemoteChannelStreamingResult {
  startRemote: () => Promise<void>
  stopRemote: () => Promise<void>
  /** Live status for the on-screen indicator/picker (idle off-desktop). */
  status: IRemoteStatus
  /** Manually capture a specific output (from the picker) — also locks. */
  setSource: (targetId: string) => Promise<void>
  /**
   * Latest observe-only classification of whether the far-end (loopback) is also
   * present in the mic — speakers (`far-end-in-mic`) vs headphones
   * (`far-end-absent`), or `unknown` before there's enough far-end audio. Derived
   * from waveform coherence; updated ~750ms once the far-end is captured;
   * `unknown` until then and off-desktop. Read-only signal for a later wave (+
   * optional UI); it drives nothing here.
   */
  farEndInMic: IEchoCoherenceResult
  /** Whether the meeting has latched to speakers-mode (loopback being suppressed).
   *  Whole-session; surfaced for the recorder indicator. */
  speakersLatched: boolean
}

export function useRemoteChannelStreaming({
  meetingId,
  companyId,
  languages,
  backend,
  attachWs,
  detachWs,
  primaryMicLabel,
  chunksRef,
  mimeTypeRef,
  offsetSecRef,
  recordingStartRef,
  streamingRef,
  micStreamRef,
  conditionalLoopbackEnabled,
  onFarEndVerdict,
  onLock,
  onStop,
}: IProps): IUseRemoteChannelStreamingResult {
  const stateRef = useRef<TRecordingState>("idle")
  const recorderRef = useRef<MediaRecorder | null>(null)
  // First remote-recorder chunk carries the WebM/Opus init header. Cached so a WS
  // auto-reconnect can replay it on the fresh socket — the running recorder only
  // emits headerless mid-stream frames, which the new server session can't parse.
  const remoteHeaderChunkRef = useRef<Blob | null>(null)
  const activeRef = useRef(false)
  const lockedRef = useRef(false)
  const currentTargetIdRef = useRef<string | undefined>(undefined)
  const targetsRef = useRef<ITapTarget[]>([])
  const switchingRef = useRef(false)
  const monitorRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const pendingRef = useRef<{ id: string; ticks: number } | null>(null)
  // Mic outputs excluded from auto-detect this session (computed from the target
  // list once it's enumerated) — the hunt never locks onto these.
  const excludedRef = useRef<Set<string>>(new Set())
  // Latest selected-mic label, read inside the (stable) target-list callback.
  const micLabelRef = useRef<string | undefined>(primaryMicLabel)
  micLabelRef.current = primaryMicLabel
  const [status, setStatus] = useState<IRemoteStatus>(IDLE_STATUS)

  // Observe-only far-end-in-mic detection. Refs so the meter closure is stable
  // and never restarts on a prop/state change.
  const micStreamSourceRef = useRef<React.MutableRefObject<MediaStream | null> | undefined>(micStreamRef)
  micStreamSourceRef.current = micStreamRef
  const farEndAudioCtxRef = useRef<AudioContext | null>(null)
  const farEndMeterIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const farEndModeRef = useRef<IEchoCoherenceResult["mode"]>("unknown")
  // Conditional-loopback (Phase 2) hysteresis + verdict state. Refs so the stable
  // meter closure sees live values without restarting.
  const conditionalLoopbackRef = useRef<boolean>(conditionalLoopbackEnabled ?? false)
  conditionalLoopbackRef.current = conditionalLoopbackEnabled ?? false
  const onFarEndVerdictRef = useRef<((speakersMode: boolean) => void) | undefined>(onFarEndVerdict)
  onFarEndVerdictRef.current = onFarEndVerdict
  const speakersStreakRef = useRef(0)
  const speakersLatchedRef = useRef(false)
  // Once true, the remote LIVE transcript WS stays closed for the session (capture
  // via REST chunks continues). Belt-and-suspenders alongside the WS close.
  const suppressRemoteLiveRef = useRef(false)
  // True once the coherence meter has actually run this session (far-end locked),
  // so we only report a verdict at stop for genuine hybrid meetings.
  const meterRanRef = useRef(false)
  const [farEndInMic, setFarEndInMic] = useState<IEchoCoherenceResult>(FAR_END_IDLE)
  // Whether the meeting has latched to speakers-mode (whole-session). Surfaced for
  // the recorder UI so the user can see the routing decision live.
  const [speakersLatched, setSpeakersLatched] = useState(false)

  const noop = useCallback((): void => {}, [])

  const { startTap, retargetTap, stopTap } = useRemoteTapStream()

  // Stable so it doesn't churn useStreamingWs's openStreamingWs identity.
  const getHeaderChunk = useCallback((): Blob | null => remoteHeaderChunkRef.current, [])

  const { wsRef, openStreamingWs, resetWsState, handleStreamingStopped } = useStreamingWs({
    meetingId,
    companyId,
    languages,
    backend,
    channel: "remote",
    getHeaderChunk,
    stateRef,
    attachWs: attachWs ?? noop,
    detachWs: detachWs ?? noop,
    setMode: noop,
    setError: noop,
  })

  const stopMonitor = useCallback((): void => {
    if (monitorRef.current) {
      clearInterval(monitorRef.current)
      monitorRef.current = null
    }
    pendingRef.current = null
  }, [])

  // Tear down the observe-only coherence meter: stop sampling, drop the analyser
  // context, reset the exposed classification.
  const stopCoherenceMeter = useCallback((): void => {
    if (farEndMeterIntervalRef.current) {
      clearInterval(farEndMeterIntervalRef.current)
      farEndMeterIntervalRef.current = null
    }
    if (farEndAudioCtxRef.current) {
      farEndAudioCtxRef.current.close().catch(() => {})
      farEndAudioCtxRef.current = null
    }
    farEndModeRef.current = "unknown"
    setFarEndInMic(FAR_END_IDLE)
  }, [])

  // Attach passive analysers to BOTH the far-end tap `loopStream` and the raw mic
  // stream (in one AudioContext so they're sample-rate-aligned) and, every
  // ~750ms, compute waveform coherence between them (see lib/coherence). These
  // are dead-end taps (source → analyser, NO destination edge — the same metering
  // the mic pipeline uses): the far-end is never routed to the speakers and
  // capture/streaming is never touched. Best-effort — no mic stream, or any
  // failure, just leaves `farEndInMic` at its idle "unknown".
  const startCoherenceMeter = useCallback(
    (loopStream: MediaStream): void => {
      stopCoherenceMeter()
      const micStream = micStreamSourceRef.current?.current
      if (!micStream) return
      try {
        const ctx = new AudioContext()
        const loopAnalyser = ctx.createAnalyser()
        const micAnalyser = ctx.createAnalyser()
        loopAnalyser.fftSize = COHERENCE_FFT_SIZE
        micAnalyser.fftSize = COHERENCE_FFT_SIZE
        ctx.createMediaStreamSource(loopStream).connect(loopAnalyser)
        // The mic stream can be HOT-SWAPPED mid-call (useAudioPipeline.reacquireMic
        // makes a fresh MediaStream on device loss/change). Track which stream the
        // mic analyser is bound to and re-bind when it changes, so the verdict never
        // silently reads a dead stream. (The loop tap keeps the same stream across a
        // retarget, so it needs no re-bind.)
        let boundMicStream: MediaStream = micStream
        let micSource = ctx.createMediaStreamSource(boundMicStream)
        micSource.connect(micAnalyser)
        farEndAudioCtxRef.current = ctx
        meterRanRef.current = true
        const micBuf = new Float32Array(COHERENCE_FFT_SIZE)
        const loopBuf = new Float32Array(COHERENCE_FFT_SIZE)
        farEndMeterIntervalRef.current = setInterval(() => {
          const liveMic = micStreamSourceRef.current?.current
          if (liveMic && liveMic !== boundMicStream) {
            try {
              micSource.disconnect()
            } catch {
              // already disconnected
            }
            boundMicStream = liveMic
            micSource = ctx.createMediaStreamSource(liveMic)
            micSource.connect(micAnalyser)
          }
          micAnalyser.getFloatTimeDomainData(micBuf)
          loopAnalyser.getFloatTimeDomainData(loopBuf)
          const next = analyzeEchoCoherence(micBuf, loopBuf, ctx.sampleRate)
          // Observe-only telemetry: log only when the verdict changes so a real
          // desktop hybrid call can be watched (speakers ⇒ far-end-in-mic,
          // headphones ⇒ far-end-absent) before a later wave consumes the signal.
          if (next.mode !== farEndModeRef.current) {
            farEndModeRef.current = next.mode
            const coh = next.diagnostics?.meanCoherence?.toFixed(2) ?? "—"
            const delay = next.diagnostics?.delaySamples ?? "—"
            console.info(`[remote-capture] far-end coherence → ${next.mode} (coherence ${coh}, delay ${delay} samples)`)
          }
          setFarEndInMic(next)

          // Phase 2 hysteresis: latch to speakers-mode after a sustained run of
          // `far-end-in-mic` verdicts, then act once. Whole-session latch.
          if (!speakersLatchedRef.current) {
            if (next.mode === "far-end-in-mic") speakersStreakRef.current += 1
            else if (next.mode === "far-end-absent") speakersStreakRef.current = 0
            // "unknown" holds the streak (a silent gap between far-end utterances).
            if (speakersStreakRef.current >= SPEAKERS_LATCH_STREAK) {
              speakersLatchedRef.current = true
              setSpeakersLatched(true)
              // Persist the verdict regardless of the switch (harmless; the backend
              // gate ANDs it with the switch).
              onFarEndVerdictRef.current?.(true)
              // Suppress the remote LIVE transcript ONLY when the switch is on. The
              // recorder + REST chunk upload keep running, so `remote.webm` is still
              // captured — capture is never gated.
              if (conditionalLoopbackRef.current) {
                suppressRemoteLiveRef.current = true
                handleStreamingStopped() // intentional stop → onclose won't reconnect
                if (wsRef.current) {
                  try {
                    wsRef.current.close(1000, "stop")
                  } catch {
                    // already closed
                  }
                  wsRef.current = null
                }
                console.info(
                  "[remote-capture] conditional-loopback: speakers-mode latched → suppressing remote live transcript (capture continues)",
                )
              }
            }
          }
        }, COHERENCE_INTERVAL_MS)
      } catch (err) {
        console.error("[remote-capture] coherence meter start failed:", err)
        stopCoherenceMeter()
      }
    },
    // handleStreamingStopped + wsRef are stable (a ref + an empty-dep callback), so
    // listing them here never restarts the meter — it only satisfies exhaustive-deps.
    [stopCoherenceMeter, handleStreamingStopped, wsRef],
  )

  // Open the full chain (tap → recorder → optional WS) on a freshly-started tap.
  // Called once, when we first lock onto an output. The recorder + REST chunk
  // persistence always run (so the far-end survives going offline); the live
  // transcript WS is opened only while streaming.
  const startCaptureChain = useCallback(
    async (targetId: string): Promise<boolean> => {
      resetWsState()
      let started: Awaited<ReturnType<typeof startTap>> = null
      try {
        started = await startTap(targetId)
      } catch (err) {
        console.error("[remote-capture] tap start failed:", err)
        return false
      }
      if (!started) return false
      currentTargetIdRef.current = started.targetId

      const mimeType = pickRecorderMimeType()
      if (!mimeType) {
        console.error("[remote-capture] no mimeType — stopping tap")
        await stopTap()
        return false
      }
      // Tell the remote uploader which mime the file is, so the blobs it stitches
      // (and replays from IDB) carry the right type.
      mimeTypeRef.current = mimeType

      // Live transcript only — gated to streaming mode. Offline / batch capture
      // proceeds with no WS; the durable file still comes from the REST chunks
      // pushed below. `suppressRemoteLiveRef` guards the re-open path after a
      // speakers-mode latch closed the WS (capture keeps running regardless).
      if (streamingRef.current && !suppressRemoteLiveRef.current) {
        openStreamingWs()
      }

      const recorder = new MediaRecorder(started.stream, {
        mimeType,
        audioBitsPerSecond: RECORDER_AUDIO_BITS_PER_SECOND,
      })
      recorderRef.current = recorder
      // Fresh recorder ⇒ a fresh WebM header is coming on the first chunk; drop the
      // previous one so a reconnect never replays a stale header from an old recorder.
      remoteHeaderChunkRef.current = null
      let chunks = 0
      recorder.ondataavailable = (e): void => {
        if (e.data.size === 0) return
        // The first chunk carries the WebM/Opus init header — cache it so a WS
        // auto-reconnect can replay it on the fresh socket (see remoteHeaderChunkRef).
        if (remoteHeaderChunkRef.current === null) {
          remoteHeaderChunkRef.current = e.data
        }
        // Always persist + queue for REST upload — this is the single source of
        // truth for {id}.remote.webm, online or offline.
        chunksRef.current.push(e.data)
        const open = wsRef.current?.readyState === WebSocket.OPEN
        chunks++
        if (chunks === 1 || chunks % 5 === 0) {
          console.info(`[remote-capture] recorder chunk #${chunks} ${e.data.size}B (ws open=${open})`)
        }
        if (open) {
          e.data
            .arrayBuffer()
            .then((buf) => wsRef.current?.send(buf))
            .catch(() => {})
        }
      }
      recorder.start(2000)
      // Observe-only: start sampling the far-end + mic envelopes for the
      // far-end-in-mic classifier. Runs on the SAME sink stream a retarget keeps
      // alive, so it survives a manual source swap without restarting.
      startCoherenceMeter(started.stream)
      console.info(
        `[remote-capture] remote channel live — channel=remote on ${targetId} (streaming=${streamingRef.current})`,
      )
      return true
    },
    [
      startTap,
      stopTap,
      openStreamingWs,
      resetWsState,
      wsRef,
      chunksRef,
      mimeTypeRef,
      streamingRef,
      startCoherenceMeter,
    ],
  )

  const stopCaptureChain = useCallback(async (): Promise<void> => {
    // Stop the remote channel's periodic-save + chunk-upload timers first so no
    // background upload races the finish-time drain.
    onStop()
    stopCoherenceMeter()
    handleStreamingStopped()
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      try {
        recorderRef.current.stop()
      } catch {
        // already stopped
      }
    }
    recorderRef.current = null
    await stopTap()
    if (wsRef.current) {
      try {
        wsRef.current.close(1000, "stop")
      } catch {
        // already closed
      }
      wsRef.current = null
    }
  }, [stopTap, handleStreamingStopped, wsRef, onStop, stopCoherenceMeter])

  // First lock: start capturing the detected/chosen output, then stop hunting.
  const lockOnto = useCallback(
    async (targetId: string, opts?: { announce?: boolean }): Promise<void> => {
      if (!activeRef.current || switchingRef.current || recorderRef.current) return
      switchingRef.current = true
      try {
        const ok = await startCaptureChain(targetId)
        if (ok) {
          lockedRef.current = true
          stopMonitor()
          // Capture-time offset: how far into the room recording the far-end
          // locked on (the hunt takes a few seconds). The backend writes this to
          // {id}.remote.offset so the playback mix + transcript merge align the
          // far-end track. Clamped ≥0; 0 when the room start is unknown.
          const startedAtMs = recordingStartRef.current
          offsetSecRef.current = startedAtMs > 0 ? Math.max(0, (Date.now() - startedAtMs) / 1000) : 0
          // The far-end is live — start its independent periodic-save + REST
          // upload timers (mirrors the mic channel).
          onLock()
          setStatus((s) => ({
            ...s,
            phase: "capturing",
            sourceId: currentTargetIdRef.current,
            sourceName: targetName(targetsRef.current, currentTargetIdRef.current),
          }))
          if (opts?.announce) {
            notifications.show({
              color: "success",
              title: "Meeting audio detected",
              message: `Capturing system audio from ${targetName(targetsRef.current, currentTargetIdRef.current)}.`,
            })
          }
        }
      } finally {
        switchingRef.current = false
      }
    },
    [startCaptureChain, stopMonitor, recordingStartRef, offsetSecRef, onLock],
  )

  // Manual re-pick while already capturing: swap only the native source (fresh
  // channel, recorder + WS stay alive) — never interrupts the recording.
  const retarget = useCallback(
    async (newId: string): Promise<void> => {
      if (!activeRef.current || switchingRef.current || newId === currentTargetIdRef.current) return
      switchingRef.current = true
      try {
        const applied = await retargetTap(newId)
        if (applied) {
          currentTargetIdRef.current = applied
          setStatus((s) => ({ ...s, sourceId: applied, sourceName: targetName(targetsRef.current, applied) }))
        }
      } finally {
        switchingRef.current = false
      }
    },
    [retargetTap],
  )

  const startMonitor = useCallback((): void => {
    if (monitorRef.current) return
    pendingRef.current = null
    monitorRef.current = setInterval(async () => {
      if (!activeRef.current || lockedRef.current || switchingRef.current) return
      const levels = await probeTapLevels(600)
      setStatus((s) => ({ ...s, levels }))
      // Drop outputs that are the microphone (virtual-mixer mic loopbacks etc.) —
      // the remote channel captures the far-end, never your own mic.
      const active = levels.filter((l) => l.peak > ACTIVE_THRESHOLD && !excludedRef.current.has(l.id))
      // Diagnostic: the top few raw peaks each tick, so a "didn't auto-detect"
      // report can be traced to actual levels vs the ACTIVE_THRESHOLD trigger.
      const top = [...levels].sort((a, b) => b.peak - a.peak).slice(0, 4)
      console.info(
        `[remote-capture] hunt peaks ${top.map((l) => l.peak.toFixed(3)).join(", ") || "(none)"} — active(>${ACTIVE_THRESHOLD}): ${active.length}, excluded mics: ${excludedRef.current.size}`,
      )
      if (active.length === 0) {
        pendingRef.current = null
        return
      }
      // Prefer the Communications endpoint when several are active; else loudest.
      const preferred = active.find((l) => targetsRef.current.find((t) => t.id === l.id)?.is_default)
      const candidate =
        preferred ?? active.reduce<ITapLevel | null>((best, l) => (l.peak > (best?.peak ?? -1) ? l : best), null)
      if (!candidate) return

      if (pendingRef.current?.id === candidate.id) pendingRef.current.ticks += 1
      else pendingRef.current = { id: candidate.id, ticks: 1 }

      if (pendingRef.current.ticks >= LOCK_TICKS) {
        console.info(`[remote-capture] locking onto ${candidate.id} (peak ${candidate.peak.toFixed(3)})`)
        await lockOnto(candidate.id, { announce: true })
      }
    }, MONITOR_INTERVAL_MS)
  }, [lockOnto])

  const startRemote = useCallback(async (): Promise<void> => {
    if (!IS_TAURI || activeRef.current) return
    activeRef.current = true
    lockedRef.current = false
    excludedRef.current.clear()
    // Fresh conditional-loopback state for the new session.
    speakersStreakRef.current = 0
    speakersLatchedRef.current = false
    suppressRemoteLiveRef.current = false
    meterRanRef.current = false
    setSpeakersLatched(false)
    stateRef.current = "recording"
    setStatus({ ...IDLE_STATUS, phase: "detecting" })

    listTapTargets().then((targets) => {
      targetsRef.current = targets
      // Exclude microphone outputs from auto-detect (virtual mixers expose the
      // mic as a render endpoint, often as the Communications default). The
      // manual picker can still select them.
      excludedRef.current = micOutputIds(targets, micLabelRef.current)
      if (excludedRef.current.size > 0) {
        const names = targets.filter((t) => excludedRef.current.has(t.id)).map((t) => t.name)
        console.info(`[remote-capture] excluding mic outputs from auto-detect: ${names.join(", ")}`)
      }
      setStatus((s) => ({ ...s, targets }))
    })

    // Hunt only — the capture chain opens when we lock onto the first real audio.
    startMonitor()
  }, [startMonitor])

  const stopRemote = useCallback(async (): Promise<void> => {
    if (!activeRef.current) return
    activeRef.current = false
    lockedRef.current = false
    excludedRef.current.clear()
    stateRef.current = "idle"
    stopMonitor()
    // Reliability backstop: re-report the verdict at meeting-end (retried by the
    // caller), so a transient failure of the on-latch report can't silently leave
    // the backend without it. ONLY when latched TRUE: this live detector's window
    // (~0.7s) is structurally blind to multi-second loopback latency, so its
    // "not latched" carries no signal — persisting `false` would overwrite the
    // column and DISABLE the backend's authoritative file-based detection (which
    // only self-triggers on a NULL verdict). Latched `true` remains trustworthy.
    if (meterRanRef.current && speakersLatchedRef.current) onFarEndVerdictRef.current?.(true)
    await stopCaptureChain()
    currentTargetIdRef.current = undefined
    setStatus(IDLE_STATUS)
  }, [stopMonitor, stopCaptureChain])

  const setSource = useCallback(
    async (targetId: string): Promise<void> => {
      if (!activeRef.current) return
      stopMonitor() // user took manual control — stop auto-hunting
      lockedRef.current = true
      if (recorderRef.current) {
        await retarget(targetId)
      } else {
        // Not capturing yet (still hunting / in-person) → force-start on this output.
        lockedRef.current = false // let lockOnto run its start
        await lockOnto(targetId)
      }
    },
    [retarget, lockOnto, stopMonitor],
  )

  // Belt-and-suspenders teardown: the parent stops the remote channel on unmount
  // (which tears the meter down via stopCaptureChain), but guarantee the interval
  // + AudioContext are released even if the hook unmounts on its own.
  useEffect(() => {
    return () => {
      stopCoherenceMeter()
    }
  }, [stopCoherenceMeter])

  return { startRemote, stopRemote, status, setSource, farEndInMic, speakersLatched }
}
